import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Select,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { 
    createWorkAWS, 
    getDocumentModelsAWS,
    getDocumentModelDetailAWS
} from '../../offchain-logic/services';
import moment from 'moment';

interface DocumentModel {
    id_model: string;
    model_name: string;
    description: string;
    display_order: number;
    slot_count: number;
}

interface Props {
    handleOpenToast: (message: string, severity: 'success' | 'error') => void;
}

export const CreateWork: React.FC<Props> = ({ handleOpenToast }) => {
    const navigate = useNavigate();
    
    const [documentModels, setDocumentModels] = useState<DocumentModel[]>([]);
    const [selectedActivityId, setSelectedActivityId] = useState<string>('');
    const [selectedModelDetail, setSelectedModelDetail] = useState<any>(null);
    
    const [formData, setFormData] = useState({
        work_name: '',
        id_activity: '',
        id_model: '',
        work_date: moment().format('DD/MM/YYYY'),
        description: '',
        status: 'in_progress'
    });

    const [errors, setErrors] = useState({
        work_name: false,
        id_model: false
    });

    useEffect(() => {
        // Leggi l'attività dall'header
        const activityId = sessionStorage.getItem('selectedActivityId');
        const selectedUserId = sessionStorage.getItem('selectedUserId'); // Per admin: utente selezionato
        
        if (activityId) {
            setSelectedActivityId(activityId);
            setFormData(prev => ({ 
                ...prev, 
                id_activity: activityId,
                ...(selectedUserId && { id_owner: selectedUserId }) // Se admin ha selezionato un utente, imposta id_owner
            }));
        }
        loadDocumentModels();
    }, []);

    const loadDocumentModels = () => {
        getDocumentModelsAWS((err: any, data: any) => {
            if (err) {
                console.error('[MODELS] Error:', err);
                handleOpenToast('Errore caricamento modelli', 'error');
                return;
            }
            
            if (data?.models) {
                setDocumentModels(data.models);
            }
        });
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({ ...prev, [field]: false }));
        }
        
        // Se cambia il modello, carica il dettaglio
        if (field === 'id_model' && value) {
            loadModelDetail(value);
        }
    };

    const loadModelDetail = (modelId: string) => {
        getDocumentModelDetailAWS(modelId, (err: any, data: any) => {
            if (err) {
                console.error('[MODEL DETAIL] Error:', err);
                handleOpenToast('Errore caricamento dettaglio modello', 'error');
                return;
            }
            
            if (data?.model) {
                setSelectedModelDetail(data.model);
            }
        });
    };

    const validateForm = () => {
        const newErrors = {
            work_name: !formData.work_name.trim(),

            id_model: !formData.id_model
        };
        
        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            handleOpenToast('Compila tutti i campi obbligatori', 'error');
            return;
        }

        createWorkAWS(formData, (err: any, data: any) => {
            
            if (err) {
                console.error('[CREATE WORK] Error:', err);
                handleOpenToast('Errore creazione pratica', 'error');
                return;
            }
            
            handleOpenToast('Pratica creata con successo', 'success');
            
            // Naviga al dettaglio del work appena creato
            if (data?.work?.id_work) {
                navigate(`/work-detail/${data.work.id_work}`);
            } else {
                navigate('/works');
            }
        });
    };

    const getSelectedModel = () => {
        return documentModels.find(m => m.id_model === formData.id_model);
    };

    if (!selectedActivityId) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', mt: 8 }}>
                <Typography variant="h5" gutterBottom color="textSecondary">
                    Nessuna attività selezionata
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    Seleziona un'attività dall'header per creare una nuova pratica
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/works')}
                >
                    Torna alla Lista
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/works')}
                    sx={{ mb: 2 }}
                >
                    Torna alla Lista
                </Button>
                
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#8B6F47' }}>
                    Crea Nuova Pratica
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Compila i dettagli della nuova pratica e seleziona il modello documenti
                </Typography>
            </Box>

            {/* Form Card */}
            <Card>
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        {/* Work Name */}
                        <Grid item xs={12}>
                            <TextField
                                label="Nome Pratica"
                                value={formData.work_name}
                                onChange={(e) => handleInputChange('work_name', e.target.value)}
                                fullWidth
                                required
                                error={errors.work_name}
                                helperText={errors.work_name ? 'Campo obbligatorio' : 'Es: Ristrutturazione Locale, Pratica SCIA, ecc.'}
                                variant="outlined"
                            />
                        </Grid>

                        {/* Document Model */}
                        <Grid item xs={12}>
                            <FormControl fullWidth required error={errors.id_model}>
                                <InputLabel>Modello Documenti</InputLabel>
                                <Select
                                    value={formData.id_model}
                                    onChange={(e) => handleInputChange('id_model', e.target.value)}
                                    label="Modello Documenti"
                                >
                                    {documentModels.map((model) => (
                                        <MenuItem key={model.id_model} value={model.id_model}>
                                            {model.model_name} {model.slot_count > 0 && `(${model.slot_count} documenti)`}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.id_model && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                                        Campo obbligatorio
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Selected Model Info */}
                        {formData.id_model && getSelectedModel() && (
                            <Grid item xs={12}>
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Modello selezionato:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {getSelectedModel()?.model_name}
                                    </Typography>
                                    {getSelectedModel()?.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {getSelectedModel()?.description}
                                        </Typography>
                                    )}
                                    {getSelectedModel()?.slot_count! > 0 && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            📋 Questo modello include {getSelectedModel()?.slot_count} slot per documenti
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        )}

                        {/* Document Slots List */}
                        {selectedModelDetail && selectedModelDetail.document_template && Object.keys(selectedModelDetail.document_template).length > 0 && (
                            <Grid item xs={12}>
                                <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            📄 Documenti da caricare
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Dopo aver creata la pratica, potrai caricare i seguenti documenti:
                                        </Typography>
                                        <List>
                                            {Object.entries(selectedModelDetail.document_template)
                                                .sort(([, a]: any, [, b]: any) => (a.slot_order || 0) - (b.slot_order || 0))
                                                .map(([slotId, slot]: any) => (
                                                    <ListItem
                                                        key={slotId}
                                                        sx={{
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            bgcolor: 'white'
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <UploadFileIcon color={slot.is_required ? 'error' : 'action'} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="body1">
                                                                        {slot.slot_name}
                                                                    </Typography>
                                                                    {slot.is_required && (
                                                                        <Chip label="Obbligatorio" size="small" color="error" />
                                                                    )}
                                                                </Box>
                                                            }
                                                            secondary={slot.description || 'Nessuna descrizione'}
                                                        />
                                                    </ListItem>
                                                ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        {/* Work Date */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Data Pratica"
                                value={formData.work_date}
                                onChange={(e) => handleInputChange('work_date', e.target.value)}
                                fullWidth
                                disabled
                                helperText="Data di creazione della pratica"
                            />
                        </Grid>

                        {/* Status */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Stato</InputLabel>
                                <Select
                                    value={formData.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                    label="Stato"
                                    disabled
                                >
                                    <MenuItem value="pending">In Attesa</MenuItem>
                                    <MenuItem value="in_progress">In Corso</MenuItem>
                                    <MenuItem value="completed">Completato</MenuItem>
                                    <MenuItem value="cancelled">Annullato</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12}>
                            <TextField
                                label="Descrizione"
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                fullWidth
                                multiline
                                rows={4}
                                helperText="Note aggiuntive sulla pratica (opzionale)"
                            />
                        </Grid>
                    </Grid>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/works')}
                            fullWidth
                        >
                            Annulla
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSubmit}
                            fullWidth
                        >
                            Crea Pratica
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};
